// Client-side fetch wrapper that automatically attaches the CSRF token
// from the smo_csrf cookie as the X-CSRF-Token header. Use this in place
// of `fetch()` for any state-changing call (POST / PATCH / DELETE / PUT)
// to /api/v1/* on the browser side.
//
// The proxy mints the cookie on every request, so it's available on any
// page that has done at least one navigation. The cookie is JS-readable
// (not httpOnly) — that's what makes the double-submit pattern work.
//
// Server-only callers (route handlers calling other route handlers, etc.)
// don't need this — they're not subject to CSRF since the browser isn't
// in the loop.

const CSRF_COOKIE_NAME = "smo_csrf";
const CSRF_HEADER_NAME = "X-CSRF-Token";

function readCsrfCookie(): string | null {
  if (typeof document === "undefined") return null;
  // Naive parse — `smo_csrf=abc123; other=…`. Handles missing cookie,
  // multiple values, and surrounding whitespace. We don't bother with
  // URL-decoding because the token is plain hex (no special chars).
  const match = document.cookie.match(new RegExp(`(?:^|; )${CSRF_COOKIE_NAME}=([^;]*)`));
  return match ? match[1] : null;
}

const SAFE_METHODS = new Set(["GET", "HEAD", "OPTIONS"]);

export async function fetchWithCsrf(
  input: RequestInfo | URL,
  init: RequestInit = {},
): Promise<Response> {
  const method = (init.method ?? "GET").toUpperCase();
  if (SAFE_METHODS.has(method)) {
    return fetch(input, init);
  }

  const token = readCsrfCookie();
  // If the cookie isn't set yet (e.g. very first request before proxy
  // mints it), let the request fly anyway. The proxy will reject it with
  // 403 and the page reload will pick up the cookie. Don't block here —
  // throwing client-side would mask the server response that tells the
  // user what went wrong.
  const headers = new Headers(init.headers);
  if (token) headers.set(CSRF_HEADER_NAME, token);

  return fetch(input, { ...init, headers });
}
